import {useMemo} from 'react';

import {DataFrame} from 'data-frames/DataFrameTypes';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrameVariables,
} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {GET_PERSISTED_DATA_FRAME_QUERY} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetScratchPlateDesignSummary,
  GetScratchPlateDesignSummaryVariables,
} from 'notebook/integrations/plate-maps/scratch-plates/__generated/GetScratchPlateDesignSummary';
import {useScratchPlateData} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateDesignSummaryUtil';
import {
  PlateRecordHydratedDataQuery,
  PlateRecordHydratedDataQueryVariables,
} from 'samples/plate-maps/plate-record/__generated/PlateRecordHydratedDataQuery';
import {PLATE_RECORD_HYDRATED_DATA_QUERY} from 'samples/plate-maps/plate-record/PlateRecordQueryUtil';
import {hydratedPlateDataFromQueryResult} from 'samples/plate-maps/plate-record/PlateRecordUtil';

import {
  buildFilledPlateMap,
  buildHydrationVariablesFromContent,
  CONTENT_LAYER_NAME,
  FilledScratchPlateData,
  parseContentDataFrame,
} from './FilledScratchPlateUtil';

export type {FilledScratchPlateData};

/**
 * The hook leans on `BenchlingQueryResult` (loading / error /
 * inaccessible-plate) for the plate fetch, and reserves domain-specific
 * statuses for the cases the raw query state can't express.
 */
export type UseFilledScratchPlateDataResult =
  | {
      kind: 'query';
      query: SkippableBenchlingQueryResult<
        GetScratchPlateDesignSummary,
        GetScratchPlateDesignSummaryVariables
      >;
    }
  | {kind: 'missing-content-layer'}
  | {kind: 'hydration-error'}
  | {kind: 'unregistered-samples'}
  | {kind: 'success'; data: FilledScratchPlateData};

const EMPTY_HYDRATION_VARIABLES: PlateRecordHydratedDataQueryVariables = {
  bioentityIds: [],
  storableIds: [],
  unitIds: [],
  unitVersionNumbers: [],
  testOrderIds: [],
};

export default function useFilledScratchPlateData(scratchPlateId: string): UseFilledScratchPlateDataResult {
  const {
    scratchPlateQuery,
    plate,
    plateMap,
    containerSchemaId,
    dataFrameIsInaccessible,
    dataFrameQueriesPending,
    dataFrameQueriesError,
  } = useScratchPlateData(scratchPlateId);

  const contentLayerDataFrameId =
    plate?.layers.find((l) => l.name === CONTENT_LAYER_NAME)?.dataFrameId ?? null;

  // TODO(BNCH-154328): paginate; today the parser
  // flags isTruncated and the viewer renders whatever placements we
  // received alongside a "some data may be missing" banner.
  const contentDataFrameQuery = useSkippableBenchlingQuery<
    GetPersistedDataFrame,
    GetPersistedDataFrameVariables
  >(GET_PERSISTED_DATA_FRAME_QUERY, {
    variables: {
      dataFrameId: contentLayerDataFrameId ?? '',
      limit: DEFAULT_DATA_FRAME_ROW_LIMIT,
    },
    skip: contentLayerDataFrameId == null,
  });

  const contentDataFrameNode =
    contentDataFrameQuery.type === QueryResultType.SUCCESS &&
    contentDataFrameQuery.data.dataFrame.__typename === 'DataFrameNode'
      ? contentDataFrameQuery.data.dataFrame
      : null;

  const contentDataFrameIsInaccessible =
    contentDataFrameQuery.type === QueryResultType.SUCCESS &&
    isInaccessible(contentDataFrameQuery.data.dataFrame);

  // `dataFrameJson` is JSONScalar (typed `unknown`); mirror the cast in PersistedDataFrameTableWrapper.
  const contentDataFrame: DataFrame | null =
    (contentDataFrameNode?.dataFrameJson as unknown as DataFrame | undefined) ?? null;

  // Use the data frame's authoritative row count for the truncation signal,
  // rather than comparing fetched-rows to the limit.
  const contentTotalRowCount = contentDataFrameNode?.numRows ?? null;

  const parsedContent = useMemo(() => {
    if (contentDataFrame == null || contentTotalRowCount == null) {
      return null;
    }
    return parseContentDataFrame(contentDataFrame, contentTotalRowCount);
  }, [contentDataFrame, contentTotalRowCount]);

  const hydrationVariables = useMemo(
    () =>
      parsedContent != null ? buildHydrationVariablesFromContent(parsedContent.contentByWellPosition) : null,
    [parsedContent]
  );

  const hydrationQuery = useSkippableBenchlingQuery<
    PlateRecordHydratedDataQuery,
    PlateRecordHydratedDataQueryVariables
  >(PLATE_RECORD_HYDRATED_DATA_QUERY, {
    variables: hydrationVariables ?? EMPTY_HYDRATION_VARIABLES,
    // Skip when there's nothing to hydrate (parse hasn't happened, or the parsed
    // content references no bioentities or units). The skipped case is treated
    // as SUCCESS-with-no-data downstream.
    skip:
      hydrationVariables == null ||
      (hydrationVariables.bioentityIds.length === 0 && hydrationVariables.unitIds.length === 0),
  });

  // Pull `data` out under SUCCESS-narrowing so it can appear in the downstream
  // `useMemo` deps without re-narrowing.
  const hydrationData = hydrationQuery.type === QueryResultType.SUCCESS ? hydrationQuery.data : undefined;

  return useMemo<UseFilledScratchPlateDataResult>(() => {
    // Any unresolved fetch state is surfaced as the default query banner.
    if (
      scratchPlateQuery.type !== QueryResultType.SUCCESS ||
      isInaccessible(scratchPlateQuery.data.scratchPlate)
    ) {
      return {kind: 'query', query: scratchPlateQuery};
    }

    const hasDataFrameFailure =
      dataFrameIsInaccessible ||
      dataFrameQueriesError ||
      contentDataFrameIsInaccessible ||
      contentDataFrameQuery.type === QueryResultType.ERROR;
    const hasHydrationFailure = hasDataFrameFailure || hydrationQuery.type === QueryResultType.ERROR;

    const isBasePlatePending = plate == null || plateMap == null || dataFrameQueriesPending;
    const canContinueHydrationPipeline = contentLayerDataFrameId != null && !hasHydrationFailure;
    const isContentDataPending =
      canContinueHydrationPipeline && (contentDataFrame == null || parsedContent == null);
    const isHydrationPending =
      canContinueHydrationPipeline && hydrationQuery.type === QueryResultType.PENDING;
    if (isBasePlatePending || isContentDataPending || isHydrationPending) {
      return {kind: 'query', query: scratchPlateQuery};
    }

    // Different error/failure outcomes after query data has settled.
    if (hasHydrationFailure) {
      return {kind: 'hydration-error'};
    }
    if (contentLayerDataFrameId == null) {
      // Filled plates always carry a `content` layer; if this fires we
      // were handed a design plate (or a malformed filled plate).
      return {kind: 'missing-content-layer'};
    }
    if (parsedContent == null) {
      return {kind: 'query', query: scratchPlateQuery};
    }
    if (parsedContent.hasUnregisteredSamples) {
      // The viewer renders registered bioentities only.
      return {kind: 'unregistered-samples'};
    }

    // hydrationQuery.type ∈ {SUCCESS, SKIPPED}; SKIPPED is treated as
    // SUCCESS-with-no-data when nothing needed hydration.
    const hydrated =
      hydrationData != null
        ? hydratedPlateDataFromQueryResult(hydrationData)
        : {schemaIdsByReagentId: {}, unitsById: {}};

    return {
      kind: 'success',
      data: {
        plateName: plate.name,
        plateMap: buildFilledPlateMap({
          basePlateMap: plateMap,
          contentByWellPosition: parsedContent.contentByWellPosition,
          schemaIdsByReagentId: hydrated.schemaIdsByReagentId,
          unitsById: hydrated.unitsById,
        }),
        containerSchemaId,
        isTruncated: parsedContent.isTruncated,
      },
    };
  }, [
    scratchPlateQuery,
    plate,
    plateMap,
    containerSchemaId,
    dataFrameIsInaccessible,
    dataFrameQueriesPending,
    dataFrameQueriesError,
    contentLayerDataFrameId,
    contentDataFrameIsInaccessible,
    contentDataFrameQuery.type,
    contentDataFrame,
    parsedContent,
    hydrationQuery.type,
    hydrationData,
  ]);
}
